import { useQuery } from "@tanstack/react-query";
import useRetryFetch from "./useRetryFetch";
import { config } from "../Environment";

interface CategoryTag {
  _id: string;
  name: string;
  type: "category" | "tag";
  active: boolean;
  [key: string]: any;
}

interface Vendor {
  _id: string;
  displayName: string;
  [key: string]: any;
}

/**
 * Shared hook for marketplace filter options (categories, tags, vendors).
 * Uses React Query for automatic deduplication and caching.
 * Both Marketplace and Vendor pages can consume the same cached data.
 */
export function useMarketplaceFilters() {
  const retryFetch = useRetryFetch();

  const categoriesQuery = useQuery<CategoryTag[]>({
    queryKey: ["marketplace", "categories"],
    queryFn: async () => {
      const response = await retryFetch(
        `${config.API_BASE}/api/categories-tags?type=category&active=true`
      );
      const result = await response.json();
      return Array.isArray(result.list) ? result.list : [];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes — filter options rarely change
  });

  const tagsQuery = useQuery<CategoryTag[]>({
    queryKey: ["marketplace", "tags"],
    queryFn: async () => {
      const response = await retryFetch(
        `${config.API_BASE}/api/categories-tags?type=tag&active=true`
      );
      const result = await response.json();
      return Array.isArray(result.list) ? result.list : [];
    },
    staleTime: 1000 * 60 * 10,
  });

  const vendorsQuery = useQuery<Vendor[]>({
    queryKey: ["marketplace", "vendors"],
    queryFn: async () => {
      const response = await retryFetch(
        `${config.API_BASE}/api/vendor/search?active=true`
      );
      const result = await response.json();
      return Array.isArray(result.list) ? result.list : [];
    },
    staleTime: 1000 * 60 * 10,
  });

  return {
    availableCategories: categoriesQuery.data ?? [],
    availableTags: tagsQuery.data ?? [],
    availableVendors: vendorsQuery.data ?? [],
    isLoading:
      categoriesQuery.isLoading ||
      tagsQuery.isLoading ||
      vendorsQuery.isLoading,
  };
}
